<script setup lang="ts">
import type { DealCard, DealFilters } from '#shared/types/deal'

definePageMeta({ layout: 'default' })

const route = useRoute()
const router = useRouter()
const { trackEvent } = useUmami()

const query = computed(() => route.query.q as string ?? '')

useSeoMeta({
  title: computed(() => query.value ? `"${query.value}" — Busca no Descontito` : 'Busca — Descontito'),
  robots: 'noindex',
})

const filters = computed<DealFilters>(() => ({
  sort: (route.query.sort as DealFilters['sort']) ?? 'hot',
  category: route.query.category as string,
  limit: 20,
}))

const config = useRuntimeConfig()

const { data, status } = await useFetch<{ items: DealCard[], total: number, hasMore: boolean }>('/deals/search', {
  baseURL: config.public.apiBaseUrl,
  query: computed(() => ({
    q: query.value,
    ...filters.value,
  })),
  key: computed(() => `search-${query.value}-${JSON.stringify(filters.value)}`),
})

watch(
  () => [query.value, data.value?.total] as const,
  ([q, total]) => {
    if (!q || total === undefined) return
    trackEvent('search-results-view', { query: q, total })
  },
  { immediate: true },
)

function setSort(sort: DealFilters['sort']) {
  trackEvent('search-sort-change', { query: query.value, sort })
  router.push({ query: { ...route.query, sort } })
}
</script>

<template>
  <div>
    <div class="mb-6">
      <h1 class="text-xl font-bold text-gray-900 dark:text-white">
        <template v-if="query">
          Resultados para "<span class="text-primary-500">{{ query }}</span>"
        </template>
        <template v-else>
          Busca de ofertas
        </template>
      </h1>
      <p v-if="data" class="text-sm text-gray-500 mt-1">
        {{ data.total }} ofertas encontradas
      </p>
    </div>

    <!-- Filtros de ordenação -->
    <div class="flex gap-2 mb-6 flex-wrap">
      <UButton
        v-for="s in [{ key: 'hot', label: '🔥 Relevância' }, { key: 'new', label: '🕐 Mais recentes' }, { key: 'price_asc', label: '💰 Menor preço' }]"
        :key="s.key"
        :color="(route.query.sort ?? 'hot') === s.key ? 'primary' : 'neutral'"
        :variant="(route.query.sort ?? 'hot') === s.key ? 'solid' : 'ghost'"
        size="sm"
        @click="setSort(s.key as DealFilters['sort'])"
      >
        {{ s.label }}
      </UButton>
    </div>

    <!-- Resultados -->
    <DealGrid
      :deals="data?.items ?? []"
      :loading="status === 'pending'"
    />
  </div>
</template>
